import React, { useState } from 'react';
import tw from 'twin.macro';
// import { css } from 'styled-components/macro';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { motion } from 'framer-motion';
import logo from "src/images/logo.png";
import styled from "styled-components";

const Container = tw.div`flex min-h-screen bg-gray-100`;
const Sidebar = tw.div`w-64 bg-white shadow-lg p-6`;
const Content = tw.div`flex-1 p-10`;
const Heading = tw.h1`text-3xl font-bold text-indigo-700 mb-6`;
const Subheading = tw.h2`text-2xl font-semibold text-indigo-600 mt-10 mb-4`;
const Text = tw.p`text-gray-700 mb-4 leading-relaxed`;
const CodeBlock = tw.pre`bg-gray-800 text-green-300 p-4 rounded-lg overflow-auto text-sm mb-6`;
const SearchInput = tw.input`w-full p-2 mb-6 border border-gray-300 rounded focus:outline-none focus:border-indigo-500`;
const divstyle = {
  padding: '20px',
};

export const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};

  img {
    ${tw`w-24 mr-3`}
  }
`;
const docs = [
  {
    id: 'upload_known_face',
    title: 'Upload Known Face',
    description: 'Uploads and encodes a known face with a given name. This image will be stored and used for future recognition. The name acts as the unique ID',
    method: 'POST',
    path: '/v1/upload_known_face',
    warning:'Face Data Warning: Uploading facial data without the clear and informed consent of the person depicted may violate privacy laws. You are solely responsible for the legality of the content you upload. All uploads are logged and traceable to your account.',
    fields: [
      { name: 'image', type: 'file', required: true, description: 'Image file of the face to be uploaded' },
      { name: 'name', type: 'string', required: true, description: 'ID to associate with the face. This acts as a unique identifier for recognition.' }
    ],
    response: {
        code: 200,
        message: "Known face uploaded successfully",
        data: []
      },
    samples: {
      curl: `curl -X POST $baseUrl/v1/upload_known_face \\
  -H "x-api-key: <YOUR API KEY>" \\
  -F "image=@/path/to/image.jpg" \\
  -F "name=John Doe"`,

      axios: `const formData = new FormData();
formData.append("image", file);
formData.append("name", "John Doe");

axios.post("$baseUrl/v1/upload_known_face", formData, {
  headers: { x-api-key: "<YOUR API KEY>" }
});`,

      dart: `var request = http.MultipartRequest("POST", Uri.parse("$baseUrl/v1/upload_known_face"))
  ..fields['name'] = 'John Doe'
  ..headers['x-api-key'] = '<YOUR API KEY>'
  ..files.add(await http.MultipartFile.fromPath('image', '/path/to/image.jpg'));

var response = await request.send();`
    }
  },
  {
    id: 'recognize_face',
    title: 'Facial Recognition',
    description: 'Identifies a face in an image by comparing it with previously uploaded known faces.',
    method: 'POST',
    path: '/v1/recognize_face',
    fields: [
      { name: 'image', type: 'file', required: true }
    ],
    response: {
        code: 200,
        message: "Operation Successful",
        data: { name: "John Doe" }
      },
    samples: {
      curl: `curl -X POST $baseUrl/v1/recognize_face \\
  -H "x-api-key: <YOUR API KEY>" \\
  -F "image=@/path/to/image.jpg"`,

      axios: `const formData = new FormData();
formData.append("image", file);

axios.post("$baseUrl/v1/recognize_face", formData, {
  headers: { x-api-key: "<YOUR API KEY>" }
});`,

      dart: `var request = http.MultipartRequest("POST", Uri.parse("$baseUrl/v1/recognize_face"))
  ..headers['x-api-key'] = '<YOUR API KEY>'
  ..files.add(await http.MultipartFile.fromPath('image', '/path/to/image.jpg'));

var response = await request.send();`
    }
  },
  {
    id: 'detect_objects',
    title: 'Object Detection',
    description: 'Detects and classifies objects in an image.',
    method: 'POST',
    path: '/v1/detect_objects',
    fields: [
      { name: 'image', type: 'file', required: true }
    ],
    response: {
        code: 200,
        message: "Operation Successful",
        objects: [
          { class: "person", score: 0.95, box: [0.1, 0.1, 0.5, 0.5] }
        ]
      },
    samples: {
      curl: `curl -X POST $baseUrl/v1/detect_objects \\
  -H "x-api-key: <YOUR API KEY>" \\
  -F "image=@/path/to/image.jpg"`,

      axios: `const formData = new FormData();
formData.append("image", file);

axios.post("$baseUrl/v1/detect_objects", formData,{
    headers: { x-api-key: "<YOUR API KEY>" }
});`,

      dart: `var request = http.MultipartRequest("POST", Uri.parse("$baseUrl/v1/detect_objects"))
  ..headers['x-api-key'] = '<YOUR API KEY>'
  ..files.add(await http.MultipartFile.fromPath('image', '/path/to/image.jpg'));

var response = await request.send();`
    }
  },
  {
    id: 'extract_text',
    title: 'Text and Entity Extraction',
    description: 'Extracts text and named entities from images. Supports domain-specific models.',
    domains:[
      'medical',
      'business',
      'legal',
      'general'
    ],
    method: 'POST',
    path: '/extract_text',
    fields: [
      { name: 'image', type: 'file', required: true },
      { name: 'domain', type: 'string', required: false, description:'The industry/scope/field of context of recognition' } // e.g. "medical", "business", etc.
    ],
    response: {
        code: 200,
        message: "Extraction Successful",
        extracted_text: "...",
        entities: [
          { entity: "David", label: "PERSON" },
          { entity: "Google", label: "ORG" }
        ]
      },
    samples: {
      curl: `curl -X POST $baseUrl/v1/extract_text \\
  -H "x-api-key: <YOUR API KEY>" \\
  -F "image=@/path/to/image.jpg" \\
  -F "domain=medical"`,

      axios: `const formData = new FormData();
formData.append("image", file);
formData.append("domain", "medical");

axios.post("$baseUrl/v1/extract_text", formData, {
    headers: { x-api-key: "<YOUR API KEY>" }
});`,

      dart: `var request = http.MultipartRequest("POST", Uri.parse("$baseUrl/v1/extract_text"))
  ..headers['x-api-key'] = '<YOUR API KEY>'
  ..fields['domain'] = 'medical'
  ..files.add(await http.MultipartFile.fromPath('image', '/path/to/image.jpg'));

var response = await request.send();`
    }
  },
  {
    id: 'advanced_extract_text',
    title: 'Advanced Entity Extraction',
    description: 'Faster and more accurate extraction of text and named entities from images. Supports domain-specific models and also works with hand written text and printed text.',
    domains:[
      'medical',
      'business',
      'legal',
      'general'
    ],
    method: 'POST',
    path: '/advanced_entity_extraction',
    fields: [
      { name: 'image', type: 'file', required: true },
      { name: 'domain', type: 'string', required: false, description:'The industry/scope/field of context of recognition' } // e.g. "medical", "business", etc.
    ],
    response: {
        code: 200,
        message: "Extraction Successful",
        data: "{\n  \"text\": \"Me posting: My audience:\",\n  \"extractions\": [\n    {\n      \"extraction_class\": \"activity\",\n      \"extraction_text\": \"Me posting\",\n      \"char_interval\": {\n        \"start_pos\": 0,\n        \"end_pos\": 10\n      },\n      \"alignment_status\": \"match_exact\",\n      \"extraction_index\": 1,\n      \"group_index\": 0,\n      \"description\": null,\n      \"attributes\": {\n        \"actor\": \"Me\"\n      }\n    },\n    {\n      \"extraction_class\": \"group\",\n      \"extraction_text\": \"My audience\",\n      \"char_interval\": {\n        \"start_pos\": 12,\n        \"end_pos\": 23\n      },\n      \"alignment_status\": \"match_exact\",\n      \"extraction_index\": 2,\n      \"group_index\": 1,\n      \"description\": null,\n      \"attributes\": {\n        \"type\": \"audience\",\n        \"possessive\": \"My\"\n      }\n    }\n  ]\n}",
      },
    samples: {
      curl: `curl -X POST $baseUrl/v1/extract_text \\
  -H "x-api-key: <YOUR API KEY>" \\
  -F "image=@/path/to/image.jpg" \\
  -F "domain=medical"`,

      axios: `const formData = new FormData();
formData.append("image", file);
formData.append("domain", "medical");

axios.post("$baseUrl/v1/extract_text", formData, {
    headers: { x-api-key: "<YOUR API KEY>" }
});`,

      dart: `var request = http.MultipartRequest("POST", Uri.parse("$baseUrl/v1/extract_text"))
  ..headers['x-api-key'] = '<YOUR API KEY>'
  ..fields['domain'] = 'medical'
  ..files.add(await http.MultipartFile.fromPath('image', '/path/to/image.jpg'));

var response = await request.send();`
    }
  }
];


export default function DocsPage() {
  const [selectedDoc, setSelectedDoc] = useState(docs[0]);
  const [search, setSearch] = useState('');

  const filteredDocs = docs.filter(doc =>
    doc.title.toLowerCase().includes(search.toLowerCase()) ||
    doc.id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Container>
      <Sidebar>
        <LogoLink href="/" >
              <img src={logo} alt="logo" width="260" height="100"/>
              {/* <span style={{color:"#6373FF"}}>Blake Technologies</span>  */}
            </LogoLink>
        <SearchInput
          placeholder="Search endpoints..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {filteredDocs.map(doc => (
          <div
            key={doc.id}
            tw="py-8 mb-3 cursor-pointer hover:text-indigo-700 text-gray-800 font-medium"
            onClick={() => setSelectedDoc(doc)}
            style={divstyle}
            
          >
            {doc.title}
          </div>
        ))}
      </Sidebar>

      <Content>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} tw="max-w-3xl mx-auto">
          <Heading>{selectedDoc.title}</Heading>
          <Text><strong>BaseUrl:</strong> <code>api.blaketechhq.com</code></Text>
          <Text><strong>Endpoint:</strong> <code>{selectedDoc.method}</code> {selectedDoc.path}</Text>
          <Text>{selectedDoc.description}</Text>

          <Subheading>Required Fields</Subheading>
          <ul tw="list-disc ml-6 mb-6">
            {selectedDoc.fields.map(f => (
              <li key={f.name}>
                <strong>{f.name}</strong> ({f.type}) {f.required && '(required)'} {f.description ? `- ${f.description}` : ''}
              </li>
            ))}
          </ul>

          {selectedDoc.domains ? <Subheading>Currently Supported Domains</Subheading> : null}
          {selectedDoc.domains ? <ul>{selectedDoc.domains.map(f => (
              <li key={f}>
                <strong>{f}</strong> 
              </li>
            ))}</ul>: null}


          <Tabs>
            <TabList>
              <Tab>cURL</Tab>
              <Tab>Axios</Tab>
              <Tab>Dart</Tab>
            </TabList>

            <TabPanel>
              <CodeBlock>{selectedDoc.samples.curl}</CodeBlock>
            </TabPanel>
            <TabPanel>
              <CodeBlock>{selectedDoc.samples.axios}</CodeBlock>
            </TabPanel>
            <TabPanel>
              <CodeBlock>{selectedDoc.samples.dart}</CodeBlock>
            </TabPanel>
          </Tabs>


          <Subheading>Sample Response</Subheading>
          <CodeBlock>{JSON.stringify(selectedDoc.response)}</CodeBlock>

          <Subheading>Disclaimer</Subheading>
          <Text>Our models are trained to perform at high accuracy, but results may not always be perfect. Blake Technologies does not guarantee 100% accuracy and assumes no liability for decisions made based on AI predictions. Always verify critical results manually.</Text>
          <Text>{selectedDoc.warning}</Text>
        </motion.div>
      </Content>
    </Container>
  );
}



